import { agentWritesApi } from '../../api/agents/agent-writes'
import {
  addTabCustomAgentRun,
  addTabCustomAgentBulkBatch
} from '../../slices/agent-orchestrator-slice'
import {
  pendingAgentWriteToEntry,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionLifecycleCapability
} from './lifecycle-registry'

export const customAgentRunLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'custom_agent_run',
  toolName: 'run_custom_agent',
  aliasToolNames: ['follow_up_custom_agent', 'bulk_agent_dispatch'],
  parseToolStart(toolUseId, input, startedAtMs) {
    if (!input || typeof input !== 'object') return []
    const params = input as Record<string, unknown>
    return [
      pendingAgentWriteToEntry({
        id: '',
        toolUseId,
        conversationId: '',
        actionKind: 'custom_agent_run',
        summary: `Run ${params.name ?? 'custom agent'}`,
        params: {
          ...params,
          instruction: params.instruction ?? params.message,
          parent_run_id: params.run_id
        },
        submittedAt: new Date(startedAtMs).toISOString()
      })
    ]
  },
  parseToolComplete() {
    return null
  },
  parseSettledToolComplete: parseSettledProposeOutput,
  scheduleHydratePending: scheduleHydratePendingAgentWrites,
  apply(toolUseId) {
    return async (dispatch) => {
      try {
        const response = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({ toolUseId, graphAffecting: false })
        ).unwrap()
        if (!response.ok)
          return {
            kind: response.retryable ? 'transport_error' : 'apply_failed',
            error: response.error ?? 'Unable to start the approved run.'
          }
        const result = response.result ?? {}
        if (typeof result.conversation_id === 'string' && typeof result.name === 'string') {
          if (typeof result.run_id === 'string')
            dispatch(
              addTabCustomAgentRun({
                id: result.conversation_id,
                run: {
                  runId: result.run_id,
                  name: result.name,
                  configId: typeof result.config_id === 'string' ? result.config_id : undefined,
                  parameters: result.parameters as Record<string, unknown> | undefined,
                  kind: result.parent_run_id ? 'follow_up' : 'chat',
                  parentRunId:
                    typeof result.parent_run_id === 'string' ? result.parent_run_id : undefined
                }
              })
            )
          if (typeof result.batch_id === 'string')
            dispatch(
              addTabCustomAgentBulkBatch({
                id: result.conversation_id,
                batch: {
                  batchId: result.batch_id,
                  name: result.name,
                  itemCount: Number(result.item_count)
                }
              })
            )
        }
        return { kind: 'success' }
      } catch (err) {
        // Processing can mean a committed run whose response was lost. Keep
        // retry actionable; the proxy reuses this proposal's dispatch identity.
        return {
          kind: 'transport_error',
          error:
            readStructuredMutationError(err)?.error ??
            'Unable to confirm the run. Retry to recover its saved identity.'
        }
      }
    }
  },
  reject(toolUseId, reason) {
    return async (dispatch) => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        return result.ok
          ? { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
          : { kind: 'reject_failed', error: result.error ?? 'Unable to deny this run.' }
      } catch (err) {
        return {
          kind: 'transport_error',
          error: readStructuredMutationError(err)?.error ?? 'Unable to save the decision.'
        }
      }
    }
  },
  buildResumeRowLine(proposal, displayNumber) {
    const summary = proposal.summary.replaceAll('"', '')
    const terminal = proposal.terminalState
    if (terminal?.kind === 'applied')
      return `${displayNumber}. ACCEPTED — "${summary}". The saved request was dispatched; acknowledge it briefly and do not start another run.`
    if (terminal?.kind === 'rejected')
      return `${displayNumber}. DENIED — "${summary}". No run started. Continue directly where useful; do not repeat the proposal or switch agents to bypass this decision.`
    if (terminal?.kind === 'apply_failed')
      return `${displayNumber}. FAILED — "${summary}"; ${terminal.error}. Do not claim a run started.`
    return `${displayNumber}. UNDECIDED — "${summary}". No run is authorized.`
  }
}
registerAgentActionLifecycle(customAgentRunLifecycleCapability)
